import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { UpfrontLicenseChangeDialogData } from "../../billing.interface";

@Component({
  selector: "app-upfront-license-change-dialog",
  templateUrl: "./upfront-license-change-dialog.component.html",
  styleUrls: ["./upfront-license-change-dialog.component.scss"],
  standalone: false,
})
export class UpfrontLicenseChangeDialogComponent {
  public sendingRequest = false;
  public licenseChange: number;
  public Math = Math;

  private readonly multiplier: number;
  private readonly vatRate: number;

  constructor(
    public dialogRef: MatDialogRef<UpfrontLicenseChangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UpfrontLicenseChangeDialogData,
  ) {
    this.multiplier = this.data.subscriptionPeriod === "yearly" ? 12 : 1;
    this.vatRate = this.data.vatRate ?? 0;

    const pendingCount = this.data.pendingLicenseCount;
    const currentCount = this.data.currentLicenseCount;

    this.licenseChange =
      pendingCount !== null && pendingCount !== undefined
        ? pendingCount - currentCount
        : 0;
  }

  get hasVat(): boolean {
    return this.vatRate > 0;
  }

  get newLicenseCount(): number {
    return this.data.currentLicenseCount + (this.licenseChange || 0);
  }

  get isIncrease(): boolean {
    return this.licenseChange > 0;
  }

  get isDecrease(): boolean {
    return this.licenseChange < 0;
  }

  get hasPriceDifference(): boolean {
    if (this.data.stripePrice === null || this.data.stripePrice === undefined) {
      return false;
    }
    const billingUnitPrice =
      this.data.subscriptionPeriod === "yearly"
        ? this.data.price * 12
        : this.data.price;
    return (
      Math.round(Number(this.data.stripePrice) * 100) !==
      Math.round(Number(billingUnitPrice) * 100)
    );
  }

  get calculatedPrice(): number {
    return this.calculatePrice(this.newLicenseCount);
  }

  get calculatedPriceWithVat(): number {
    const price = this.calculatedPrice;
    const withVat = this.hasVat ? price + this.calculateVat(price) : price;
    return parseFloat(withVat.toFixed(2));
  }

  get proratedAmount(): number {
    if (!this.isIncrease) {
      return 0;
    }
    return this.calculatePrice(this.licenseChange);
  }

  get proratedAmountWithVat(): number {
    if (!this.isIncrease) {
      return 0;
    }
    const amount = this.proratedAmount;
    const withVat = this.hasVat ? amount + this.calculateVat(amount) : amount;
    return parseFloat(withVat.toFixed(2));
  }

  get vatAmount(): number {
    return this.hasVat ? this.calculateVat(this.calculatedPrice) : 0;
  }

  get proratedVatAmount(): number {
    return this.hasVat ? this.calculateVat(this.proratedAmount) : 0;
  }

  get pricePerLicense(): number {
    const raw =
      this.data.subscriptionPeriod === "yearly"
        ? this.data.price * 12
        : this.data.price;
    return parseFloat(raw.toFixed(2));
  }

  get stripePriceRounded(): number | null {
    if (this.data.stripePrice == null) {
      return null;
    }
    return parseFloat(Number(this.data.stripePrice).toFixed(2));
  }

  get formattedLicenseChange(): string {
    if (this.licenseChange === null || this.licenseChange === undefined) {
      return "";
    }
    return this.licenseChange > 0
      ? `+${this.licenseChange}`
      : String(this.licenseChange);
  }

  get canSubmit(): boolean {
    if (this.data.price <= 0) {
      return false;
    }
    return !(
      this.licenseChange === 0 &&
      !this.data.hasPeriodDifference &&
      !this.hasPriceDifference
    );
  }

  private calculatePrice(licenseCount: number): number {
    return parseFloat(
      (this.data.price * licenseCount * this.multiplier).toFixed(2),
    );
  }

  private calculateVat(amount: number): number {
    return parseFloat((amount * (this.vatRate / 100)).toFixed(2));
  }

  submit = (): void => {
    if (!this.canSubmit) {
      return;
    }

    this.sendingRequest = true;
    const hasPeriodDifference = this.data.hasPeriodDifference || false;

    let effectiveLicenseCount: number;
    if (hasPeriodDifference && this.licenseChange === 0) {
      effectiveLicenseCount = this.data.currentLicenseCount;
    } else {
      effectiveLicenseCount = this.newLicenseCount;
    }

    const hasLicenseChange =
      effectiveLicenseCount !== this.data.currentLicenseCount;

    this.dialogRef.close({
      newLicenseCount:
        hasLicenseChange || hasPeriodDifference
          ? effectiveLicenseCount
          : undefined,
      proceed: true,
      isIncrease: this.isIncrease,
      isDecrease: this.isDecrease,
      syncPrice: this.hasPriceDifference,
      hasPeriodDifference,
      needsSchedule:
        hasLicenseChange || this.hasPriceDifference || hasPeriodDifference,
    });
  };

  close = (): void => this.dialogRef.close({ proceed: false });
}
